import { css } from '@emotion/react';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { noop } from 'lodash-es';
import { useMemo } from 'react';
import { FieldSelect } from '../../../components/Form/Display/FieldSelect';
import { RendererType } from '../../../data-models/field-definition';
import { DataPointPeriodicity } from '../../../schemas/CompanyPeriodRequest.schema';
import { createFormField } from '../../../view-models/form.view-model';
import { ALL_PERIODS, AvailablePeriod } from './manage-request-utils';

const FREQUENCY_OPTIONS: { value: DataPointPeriodicity; label: string }[] = [
  { value: DataPointPeriodicity.month, label: 'Month' },
  { value: DataPointPeriodicity.quarter, label: 'Quarter' },
  { value: DataPointPeriodicity.year, label: 'Year' },
];

const wrapperCss = css`
  align-items: center;
  display: grid;
  gap: 0.75rem;
  grid-template-columns: auto 200px;

  /* The period select's TextField carries MUI's asymmetric "dense" margin
     (top 8 / bottom 4); with center alignment that drops the field a couple of
     px below the toggle. Zero it so the two line up on the row center. */
  .MuiFormControl-root {
    margin-top: 0;
    margin-bottom: 0;
  }
`;

interface FrequencyPeriodFilterProps {
  selectedFrequency: DataPointPeriodicity;
  onFrequencyChange: (frequency: DataPointPeriodicity) => void;
  selectedPeriod: string;
  onPeriodChange: (period: string) => void;
  availablePeriods: AvailablePeriod[];
}

export function FrequencyPeriodFilter({
  selectedFrequency,
  onFrequencyChange,
  selectedPeriod,
  onPeriodChange,
  availablePeriods,
}: FrequencyPeriodFilterProps) {
  const onFrequencyToggle = (_: React.MouseEvent<HTMLElement>, value: DataPointPeriodicity | null) => {
    // Exclusive group — keep one selected; ignore deselection clicks.
    if (value !== null) {
      onFrequencyChange(value);
    }
  };

  const periodField = useMemo(
    () =>
      createFormField({
        key: 'period',
        label: 'period',
        renderer: RendererType.singleSelect,
        fullWidth: true,
        // "All periods" is the cleared state, so no clear (x) button is needed.
        disableClearable: true,
        placeholder: 'Select Period',
        rendererMeta: {
          values: [
            { value: ALL_PERIODS, displayName: 'All periods' },
            ...availablePeriods.map((period) => ({ value: period.value, displayName: period.label })),
          ],
        },
      }),
    [availablePeriods]
  );

  return (
    <div css={wrapperCss}>
      <ToggleButtonGroup
        color='secondary'
        value={selectedFrequency}
        onChange={onFrequencyToggle}
        exclusive
        aria-label='Select reporting frequency'
      >
        {FREQUENCY_OPTIONS.map((option) => (
          <ToggleButton key={option.value} value={option.value}>
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <FieldSelect
        formField={periodField}
        formProps={{
          name: 'period',
          onBlur: noop,
          value: selectedPeriod,
          onChange: (value: string | undefined) => onPeriodChange(value ?? ALL_PERIODS),
          ref: noop,
        }}
      />
    </div>
  );
}
